import { useEffect, useState } from "react";
import { api, deletedTrip, type JoinPreview, type Me } from "./api.ts";
import { early, joinKey } from "./early.ts";
import { failureOf } from "./failure.ts";
import { tripDates } from "./format.ts";
import { asLabel } from "./former.tsx";
import { GettingStarted } from "./GettingStarted.tsx";
import { useLoad } from "./hooks.ts";
import { usePersonEvents } from "./me-live.ts";
import { Failed, Opening } from "./opening.tsx";
import { ReportThis } from "./ReportThis.tsx";
import { navigate, tripPath } from "./router.ts";
import { Button, Icon } from "./souvenir.ts";
import { Message, Wordmark } from "./ui.tsx";

// Where a join link lands: which trip it is, and a way to ask. The owner decides. Without an AI yet,
// asking comes first (so the owner hears at once), then getting started, in the trip's words: How
// Tripmo works, connecting an AI, and Open <trip> once they're let in. Connecting skipped (now, or before),
// it waits here for the owner and opens the trip the moment they let them in.
export function JoinPage({
  code,
  me,
  starting,
  onConnected,
  onStarted,
}: {
  code: string;
  me: Me;
  // Getting started: no AI connected yet, or just connected on this page and not tapped through.
  starting: boolean;
  onConnected: () => void;
  onStarted: () => void;
}) {
  // Bumped by Try again.
  const [tries, setTries] = useState(0);
  const preview = useLoad(() => early(joinKey(code), () => api.join(code)), [code, tries]);
  const goneTitle = preview.state === "failed" ? deletedTrip(preview.error)?.title : undefined;
  useEffect(() => {
    if (goneTitle) document.title = `${goneTitle} was deleted · Tripmo`;
  }, [goneTitle]);
  // Until the trip it's for is in, the sunrise (features/opening.md).
  if (preview.state === "loading") return <Opening />;
  if (preview.state === "failed") {
    // No signal, or Tripmo not answering: not the link's fault, so said as it is, with Try again.
    const failure = failureOf(preview.error);
    if (failure)
      return (
        <Failed
          failure={failure}
          what="join"
          again={() => setTries((n) => n + 1)}
          retrying={preview.retrying === true}
        />
      );
    // Its trip was deleted: who deleted it, and nothing to join.
    const gone = deletedTrip(preview.error);
    if (gone)
      return (
        <Message
          title={`${asLabel(gone.by.name)} deleted this trip`}
          body={`${gone.title} isn't there to join any more.`}
          action={
            <a className="tm-link body tm-join-link" href="/">
              Your trips
            </a>
          }
        />
      );
    return (
      <Message
        title="This link doesn't open a trip"
        body="It may have been replaced by a new one. Ask whoever sent it for the current link."
        action={
          <a className="tm-link body tm-join-link" href="/">
            Your trips
          </a>
        }
      />
    );
  }
  return (
    <Join
      code={code}
      preview={preview.data}
      me={me}
      starting={starting}
      onConnected={onConnected}
      onStarted={onStarted}
    />
  );
}

function Join({
  code,
  preview,
  me,
  starting,
  onConnected,
  onStarted,
}: {
  code: string;
  preview: JoinPreview;
  me: Me;
  starting: boolean;
  onConnected: () => void;
  onStarted: () => void;
}) {
  // Where I am with it: in, asking, turned down, or not asking (never, or I took my request back here).
  const [status, setStatus] = useState<JoinPreview["you"] | "withdrawn">(preview.you);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { trip, owner } = preview;

  useEffect(() => {
    document.title = `Join ${trip.title} · Tripmo`;
    if (status === "member") navigate(tripPath(trip.id), { replace: true });
  }, [status, trip]);
  // Turned down, said here: not said again on Your trips.
  useEffect(() => {
    if (status === "turned_down") api.turnDownsSeen([trip.id]).catch(() => {});
  }, [status, trip]);
  // No longer asking, heard live: turned down by the owner (or taken back on another device). Which, the trip's
  // join preview says.
  const gone = () =>
    api
      .join(code)
      .then((now) => setStatus((was) => (was === "requested" ? now.you : was)))
      .catch(() => {});
  // Waiting here (getting started, while it shows, listens for itself): let in, on to the trip; turned down, said.
  usePersonEvents(
    {
      trips: ({ trips }) => {
        const mine = trips.find((t) => t.id === trip.id);
        if (mine?.status === "member") setStatus("member");
        else if (!mine) gone();
      },
    },
    status === "requested" && !starting,
  );

  if (status === "requested" && starting)
    return (
      <GettingStarted
        me={me}
        joining={{ id: trip.id, title: trip.title, owner: owner.name, onGone: gone }}
        onConnected={onConnected}
        onFinish={onStarted}
      />
    );
  const ask = async () => {
    setBusy(true);
    setError(null);
    try {
      const asked = await api.askToJoin(code);
      setStatus(asked.status === "member" ? "member" : "requested");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  // Taking my request back (features/trips-and-members.md, "Withdraw a join request, and hear a turn-down").
  const withdraw = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.withdrawRequest(trip.id);
      setStatus("withdrawn");
    } catch (e) {
      setError((e as Error).message);
      // Let in or turned down meanwhile: the page as it is now.
      gone();
    } finally {
      setBusy(false);
    }
  };
  const failed = error && (
    <p className="callout tm-error" role="alert">
      {error}
      <ReportThis />
    </p>
  );
  const dates = tripDates(trip.dates);
  const people = preview.members === 1 ? "1 person" : `${preview.members} people`;
  return (
    <main className="tm-join">
      <Wordmark />
      {dates && <span className="eyebrow tm-sun-text">{dates}</span>}
      <h1 className="display-l">{trip.title}</h1>
      {status === "requested" && preview.locked ? (
        // Asked before it became read-only: the request stays, but nobody can be let in until it can change.
        <>
          <p className="body tm-muted">
            You've asked to join. The trip can't change for now, so {owner.name} can't let anyone in until it can.
          </p>
          <div className="tm-join-actions">
            <a className="tm-link body tm-join-link" href="/">
              Your trips
            </a>
            <Button variant="quiet" size="sm" className="tm-join-withdraw" disabled={busy} onClick={withdraw}>
              {busy ? "Withdrawing…" : "Withdraw request"}
            </Button>
          </div>
          {failed}
        </>
      ) : status === "requested" ? (
        <>
          <p className="tm-connected-wait tm-join-wait" aria-live="polite">
            <span className="tm-wait-dot" aria-hidden="true" />
            <span className="body">Waiting for {owner.name} to let you in</span>
          </p>
          <p className="body tm-muted">You've asked to join. The trip opens here once {owner.name} lets you in.</p>
          {preview.full && <p className="body tm-muted">The trip is full right now; its owner can add more spots.</p>}
          <div className="tm-join-actions">
            <a className="tm-link body tm-join-link" href="/">
              Your trips
            </a>
            <Button variant="quiet" size="sm" className="tm-join-withdraw" disabled={busy} onClick={withdraw}>
              {busy ? "Withdrawing…" : "Withdraw request"}
            </Button>
          </div>
          {failed}
        </>
      ) : status === "turned_down" || status === "withdrawn" ? (
        // Turned down (live, or as the page opens), or taken back here: said, and asking again stays possible,
        // unless the trip can't take anyone new for now.
        <>
          <p className="tm-join-out" role="status">
            <Icon name="close" size={20} className={`tm-join-out-icon${status === "withdrawn" ? " is-mine" : ""}`} />
            <span className="body-strong">
              {status === "turned_down" ? `${owner.name} didn't let you in.` : "You took back your request to join."}
            </span>
          </p>
          <p className="body tm-muted">
            {preview.locked
              ? "The trip can't change for now, so nobody new can ask to join it."
              : status === "turned_down"
                ? `If you think that's a mistake, check with ${owner.name}, then ask again.`
                : `${owner.name} won't see it any more. You can ask again whenever you like.`}
          </p>
          {!preview.locked && (
            <Button variant="secondary" block disabled={busy} onClick={ask}>
              Ask to join again
            </Button>
          )}
          {failed}
          <a className="tm-link body tm-join-link" href="/">
            Your trips
          </a>
        </>
      ) : preview.locked ? (
        // Read-only: no asking (the server refuses it), and the page says why in a line.
        <>
          <p className="body tm-muted">
            {owner.name}'s trip · {people} so far. It can't change for now, so nobody new can ask to join it.
          </p>
          <a className="tm-link body tm-join-link" href="/">
            Your trips
          </a>
        </>
      ) : (
        <>
          <p className="body tm-muted">
            {owner.name}'s trip · {people} so far. Ask to join, and {owner.name} decides.
          </p>
          {preview.full && <p className="body tm-muted">The trip is full right now; its owner can add more spots.</p>}
          <Button variant="primary" block disabled={busy} onClick={ask}>
            Ask to join
          </Button>
          {failed}
        </>
      )}
    </main>
  );
}
